export const M3_UNIT_13 = {
  id: "m3-u13-adverbs-manner",
  title: "Unit 13: Adverbs of Manner",
  topic: "Master how to describe actions with adverbs, distinguishing them from adjectives like a native speaker",
  iconType: "zap",
  grammarImage: "/m3-u13-adverbs.jpg",
  grammarText: "Learn to use adverbs of manner (slowly, well, fast) and distinguish them from adjectives. Master native phrases and real-life usage that migrants need daily. A2-B1 level with practical, street-smart English.",
  sets: [
    // ===================================================================
    // SET 1: Regular Adverbs - Basic (-ly endings) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u13-set1",
      title: "Regular Adverbs (-ly)",
      questions: [
        {
          type: "mc",
          q: "I am very lazy. I walk _____.",
          options: ["slowly", "slow", "am slow"],
          a: "slowly",
          successtip: "✓ SLOWLY (adverb) = HOW you walk! Natives say 'take it easy' when you do things slowly! 🐌 Real talk: slow = you, slowly = how you move!"
        },
        {
          type: "mc",
          q: "She speaks English _____. I can understand her!",
          options: ["clearly", "clear", "is clear"],
          a: "clearly",
          successtip: "✓ CLEARLY (adverb) = HOW she speaks! Natives say 'loud and clear' when they understand! 🗣️ Street English: clear voice = clearly!"
        },
        {
          type: "mc",
          q: "He drives very _____. He never has accidents.",
          options: ["carefully", "careful", "is careful"],
          a: "carefully",
          successtip: "✓ CAREFULLY (adverb) = HOW he drives! Natives say 'drive safe' meaning carefully! 🚗 Real life: careful driver drives carefully!"
        },
        {
          type: "mc",
          q: "My boss is very strict. He talks _____.",
          options: ["seriously", "serious", "is serious"],
          a: "seriously",
          successtip: "✓ SERIOUSLY (adverb) = HOW he talks! Natives say 'no joke' or 'for real' meaning serious! 💼 Boss talk = seriously!"
        },
        {
          type: "mc",
          q: "She ___ very quiet, so she speaks _____.",
          options: ["is / quietly", "speaks / quiet", "is / quiet"],
          a: "is / quietly",
          successtip: "✓ IS quiet (adjective) + speaks QUIETLY (adverb)! Street talk: 'keep it down' = speak quietly! 🤫 Different grammar, same person!"
        },
        {
          type: "mc",
          q: "I work _____ because I need the money.",
          options: ["hard", "hardly", "harder"],
          a: "hard",
          successtip: "✓ HARD (adverb, irregular!) = HOW you work! Natives say 'hustle hard' or 'grind hard'! 💪 Migrant reality: work hard = survive!"
        },
        {
          type: "mc",
          q: "The baby is sleeping. Please walk _____.",
          options: ["quietly", "quiet", "silence"],
          a: "quietly",
          successtip: "✓ QUIETLY (adverb) = HOW to walk! Parents say 'shh!' or 'keep it down'! 👶 Real homes: quiet baby needs quietly walking!"
        },
        {
          type: "mc",
          q: "My grandmother is very _____. She moves _____.",
          options: ["slow / slowly", "slowly / slow", "slow / slow"],
          a: "slow / slowly",
          successtip: "✓ IS slow (adjective) + moves SLOWLY (adverb)! Respectful talk: 'take your time' for elderly! 👵 Slow person = slowly moves!"
        },
        {
          type: "mc",
          q: "He answered the question _____. He's smart!",
          options: ["quickly", "quick", "is quick"],
          a: "quickly",
          successtip: "✓ QUICKLY (adverb) = HOW he answered! Natives say 'in a flash' or 'real quick'! ⚡ Smart people think quickly!"
        },
        {
          type: "mc",
          q: "She is a _____ person. She does everything _____.",
          options: ["careful / carefully", "carefully / careful", "careful / careful"],
          a: "careful / carefully",
          successtip: "✓ CAREFUL person (adjective) does things CAREFULLY (adverb)! Safety first! 🦺 Immigrant wisdom: be careful, work carefully!"
        }
      ]
    },

    // ===================================================================
    // SET 2: Irregular Adverbs - Good/Well/Fast (10 preguntas)
    // ===================================================================
    {
      id: "m3-u13-set2",
      title: "Irregular Adverbs",
      questions: [
        {
          type: "mc",
          q: "She is a _____ singer. She sings _____.",
          options: ["good / well", "well / good", "good / good"],
          a: "good / well",
          successtip: "✓ GOOD singer (adjective) sings WELL (adverb)! NEVER 'sings good'! 🎤 Native rule #1: I speak English WELL, not good!"
        },
        {
          type: "mc",
          q: "I can speak English _____. I practice every day!",
          options: ["well", "good", "goodly"],
          a: "well",
          successtip: "✓ Speak WELL (adverb)! NO 'goodly' exists! 💬 Job interview tip: 'I speak English well' gets you hired!"
        },
        {
          type: "mc",
          q: "He is a _____ runner. He runs very _____.",
          options: ["fast / fast", "fast / fastly", "quick / quickly"],
          a: "fast / fast",
          successtip: "✓ FAST = both adjective AND adverb! NO 'fastly'! 🏃 Street racing talk: 'that car is fast' AND 'drives fast'!"
        },
        {
          type: "mc",
          q: "My car is very _____. It goes _____ on the highway.",
          options: ["fast / fast", "fast / fastly", "quickly / quick"],
          a: "fast / fast",
          successtip: "✓ FAST car (adjective) goes FAST (adverb)! Same word, different use! 🚗 Real driving: fast = fast, no -ly needed!"
        },
        {
          type: "mc",
          q: "She cooks very _____. The food is always _____!",
          options: ["well / good", "good / well", "well / well"],
          a: "well / good",
          successtip: "✓ Cooks WELL (adverb) = good food (adjective)! Restaurant talk: 'chef cooks well' = 'food tastes good'! 👨‍🍳 Different words!"
        },
        {
          type: "mc",
          q: "I don't speak Spanish very _____. I need practice!",
          options: ["well", "good", "goodly"],
          a: "well",
          successtip: "✓ Speak WELL (adverb)! Migrant reality: 'I don't speak well YET' = honest immigrant talk! 🌎 Practice makes perfect!"
        },
        {
          type: "mc",
          q: "My boss is a _____ person, but he treats us _____.",
          options: ["good / well", "well / good", "good / good"],
          a: "good / well",
          successtip: "✓ GOOD person (adjective) treats WELL (adverb)! Work culture: good boss = treats employees well! 👔 Important difference!"
        },
        {
          type: "mc",
          q: "He works very _____, so he gets paid _____.",
          options: ["hard / well", "hardly / well", "hard / good"],
          a: "hard / well",
          successtip: "✓ Works HARD (adverb) = paid WELL (adverb)! Hustle culture: hard work pays off! 💰 Immigrant grind: work hard = live well!"
        },
        {
          type: "mc",
          q: "The test is very _____. I can't do it _____!",
          options: ["hard / well", "hard / good", "hardly / well"],
          a: "hard / well",
          successtip: "✓ HARD test (adjective), can't do WELL (adverb)! Student stress: hard = difficult! 📝 Real struggle: hard tests are hard!"
        },
        {
          type: "mc",
          q: "She is _____ at math. She solves problems _____.",
          options: ["good / quickly", "well / quickly", "good / quick"],
          a: "good / quickly",
          successtip: "✓ GOOD at (adjective phrase) + solves QUICKLY (adverb)! School talk: good at = talented! ✏️ Fast thinker solves quickly!"
        }
      ]
    },

    // ===================================================================
    // SET 3: Adjective vs Adverb Context (10 preguntas)
    // ===================================================================
    {
      id: "m3-u13-set3",
      title: "Adjective vs Adverb",
      questions: [
        {
          type: "mc",
          q: "My sister has a beautiful turtle. It is very _____.",
          options: ["slow", "slowly", "slows"],
          a: "slow",
          successtip: "✓ IS slow (adjective) describes the TURTLE! Slowly = movement! 🐢 Pet talk: slow turtle moves slowly!"
        },
        {
          type: "mc",
          q: "The turtle is slow, so it moves _____.",
          options: ["slowly", "slow", "is slow"],
          a: "slowly",
          successtip: "✓ Moves SLOWLY (adverb) = HOW it moves! Nature fact: slow animals move slowly! 🐌 Verb needs adverb!"
        },
        {
          type: "mc",
          q: "I am very tired. I work _____.",
          options: ["slowly", "slow", "am slow"],
          a: "slowly",
          successtip: "✓ Work SLOWLY (adverb) when tired! Worker reality: tired = work slow! 😴 Adjective (tired) affects adverb (slowly)!"
        },
        {
          type: "mc",
          q: "My dad is a _____ driver. He drives _____.",
          options: ["careful / carefully", "carefully / careful", "careful / careful"],
          a: "careful / carefully",
          successtip: "✓ CAREFUL driver (adjective) drives CAREFULLY (adverb)! Dad advice: 'drive safe!' = drive carefully! 🚙 Safety first!"
        },
        {
          type: "mc",
          q: "She is _____, so she speaks _____.",
          options: ["loud / loudly", "loudly / loud", "loud / loud"],
          a: "loud / loudly",
          successtip: "✓ IS loud (adjective person) speaks LOUDLY (adverb action)! Apartment complaints: loud neighbor talks loudly! 🔊 Different grammar!"
        },
        {
          type: "mc",
          q: "He is a _____ student. He studies _____.",
          options: ["serious / seriously", "seriously / serious", "serious / serious"],
          a: "serious / seriously",
          successtip: "✓ SERIOUS student (adjective) studies SERIOUSLY (adverb)! School success: serious students study seriously! 📚 Hard work pays!"
        },
        {
          type: "mc",
          q: "The music is very _____. I can hear it _____.",
          options: ["loud / clearly", "loudly / clear", "loud / clear"],
          a: "loud / clearly",
          successtip: "✓ IS loud (adjective music) + hear CLEARLY (adverb action)! Concert vibes: loud music, clear sound! 🎵 Two different words!"
        },
        {
          type: "mc",
          q: "My English is not very _____. I don't speak _____.",
          options: ["good / well", "well / good", "good / good"],
          a: "good / well",
          successtip: "✓ English IS good (adjective) + speak WELL (adverb)! Learner honesty: 'not good yet, but improving!' 🌍 Keep practicing!"
        },
        {
          type: "mc",
          q: "She is _____ because she walks _____.",
          options: ["slow / slowly", "slowly / slow", "slow / slow"],
          a: "slow / slowly",
          successtip: "✓ IS slow (adjective state) BECAUSE walks SLOWLY (adverb action)! Cause & effect: slow person = slow walk! 🚶 Logic!"
        },
        {
          type: "mc",
          q: "The teacher is very _____. She explains things _____.",
          options: ["patient / patiently", "patiently / patient", "patient / patient"],
          a: "patient / patiently",
          successtip: "✓ PATIENT teacher (adjective) explains PATIENTLY (adverb)! Good teachers: patient person explains patiently! 👩‍🏫 Learning needs patience!"
        }
      ]
    },

    // ===================================================================
    // SET 4: Real-Life Situations (10 preguntas)
    // ===================================================================
    {
      id: "m3-u13-set4",
      title: "Real-Life Adverbs",
      questions: [
        {
          type: "mc",
          q: "At work, I talk _____ to customers. It's professional.",
          options: ["politely", "polite", "am polite"],
          a: "politely",
          successtip: "✓ Talk POLITELY (adverb) = customer service! Job reality: smile, be polite, speak politely! 💼 Tips depend on it!"
        },
        {
          type: "mc",
          q: "I work the night shift, so I drive home _____.",
          options: ["sleepily", "sleepy", "am sleepy"],
          a: "sleepily",
          successtip: "✓ Drive SLEEPILY (adverb) when tired! Night shift struggle: sleepy people drive sleepily! 🌙 Be careful! Coffee helps!"
        },
        {
          type: "mc",
          q: "My baby cries very _____. The neighbors complain!",
          options: ["loudly", "loud", "is loud"],
          a: "loudly",
          successtip: "✓ Cries LOUDLY (adverb action)! Parent life: loud baby cries loudly at 3am! 👶 Apartment struggles are real!"
        },
        {
          type: "mc",
          q: "I save money _____. I put $50 in the bank every week.",
          options: ["regularly", "regular", "am regular"],
          a: "regularly",
          successtip: "✓ Save REGULARLY (adverb) = every week! Financial advice: save regularly, live comfortably! 💰 Immigrant wisdom: small amounts, big future!"
        },
        {
          type: "mc",
          q: "She answered the immigration officer _____. No problems!",
          options: ["honestly", "honest", "is honest"],
          a: "honestly",
          successtip: "✓ Answered HONESTLY (adverb)! Border advice: answer honestly, no lies! 🛂 Truth = smooth process! Important!"
        },
        {
          type: "mc",
          q: "I learn English _____ because I practice every day.",
          options: ["quickly", "quick", "am quick"],
          a: "quickly",
          successtip: "✓ Learn QUICKLY (adverb) with practice! Language hack: daily practice = learn quickly! 📖 Consistency beats talent!"
        },
        {
          type: "mc",
          q: "He fixed my car _____. I'm very happy!",
          options: ["perfectly", "perfect", "is perfect"],
          a: "perfectly",
          successtip: "✓ Fixed PERFECTLY (adverb)! Mechanic praise: good mechanic fixes perfectly! 🔧 Perfect work = perfect result!"
        },
        {
          type: "mc",
          q: "I wake up _____ for work. My alarm is at 5am!",
          options: ["early", "earlier", "am early"],
          a: "early",
          successtip: "✓ Wake up EARLY (adverb, irregular!)! Working life: wake early, work early, rest early! ⏰ Same word, different use!"
        },
        {
          type: "mc",
          q: "She types very _____. She's a professional secretary.",
          options: ["fast", "fastly", "is fast"],
          a: "fast",
          successtip: "✓ Types FAST (adverb, irregular!)! Office skills: fast typer types fast! ⌨️ NO 'fastly'! Speed = job security!"
        },
        {
          type: "mc",
          q: "I listen _____ when my boss talks. I need this job!",
          options: ["carefully", "careful", "am careful"],
          a: "carefully",
          successtip: "✓ Listen CAREFULLY (adverb)! Work survival: careful listener listens carefully! 👂 Boss talks = you listen! Job 101!"
        }
      ]
    },

    // ===================================================================
    // SET 5: Integration with Can (10 preguntas)
    // ===================================================================
    {
      id: "m3-u13-set5",
      title: "Can + Adverbs",
      questions: [
        {
          type: "mc",
          q: "I can speak English _____. I practice every day!",
          options: ["well", "good", "goodly"],
          a: "well",
          successtip: "✓ CAN speak WELL! Job interviews: 'I can speak English well' = hired! 💼 Never 'speak good'! Practice pays!"
        },
        {
          type: "mc",
          q: "She can't run very _____. She has bad knees.",
          options: ["fast", "fastly", "quick"],
          a: "fast",
          successtip: "✓ CAN'T run FAST! Health reality: bad knees = can't run fast! 🏃❌ Fast (adverb, no -ly)!"
        },
        {
          type: "mc",
          q: "I can type _____, but I can't spell _____.",
          options: ["quickly / well", "quick / good", "quickly / good"],
          a: "quickly / well",
          successtip: "✓ Type QUICKLY + spell WELL (both adverbs)! Office truth: fast typing ≠ good spelling! ⌨️ Two different skills!"
        },
        {
          type: "mc",
          q: "He can drive, but he can't park _____.",
          options: ["well", "good", "goodly"],
          a: "well",
          successtip: "✓ CAN'T park WELL! Parking struggles are real! 🅿️ Driver's ed: drive well, park well = license!"
        },
        {
          type: "mc",
          q: "I can work _____ because I need the money.",
          options: ["hard", "hardly", "harder"],
          a: "hard",
          successtip: "✓ CAN work HARD! Hustle culture: work hard, earn more! 💪 Irregular adverb! Immigrant grind!"
        },
        {
          type: "mc",
          q: "She can sing very _____, but she's shy.",
          options: ["well", "good", "goodly"],
          a: "well",
          successtip: "✓ CAN sing WELL! Hidden talent: shy person sings well! 🎤 Good singer = sings well!"
        },
        {
          type: "mc",
          q: "I can't hear you. Can you speak more _____?",
          options: ["loudly", "loud", "louder"],
          a: "loudly",
          tip: "When you can't hear someone, ask them to speak MORE LOUDLY. Native shortcut: 'speak up!' 🔊",
          successtip: "✓ Speak LOUDLY (adverb)! Street phrase: 'speak up!' = speak louder! 📢 Loud = adjective, loudly = how you speak!"
        },
        {
          type: "mc",
          q: "We can finish the work _____ if we work together.",
          options: ["quickly", "quick", "quicker"],
          a: "quickly",
          successtip: "✓ Finish QUICKLY (adverb)! Teamwork: together = work quickly! ⚡ Quick finish with quick work!"
        },
        {
          type: "mc",
          q: "I can dance, but I can't dance very _____.",
          options: ["well", "good", "goodly"],
          a: "well",
          successtip: "✓ CAN'T dance WELL! Honest talk: 'I dance, but not well!' 💃 Good dancer dances well!"
        },
        {
          type: "mc",
          q: "He can read English _____, but he can't write _____.",
          options: ["well / well", "good / good", "well / good"],
          a: "well / well",
          successtip: "✓ Read WELL + write WELL (both adverbs)! Learning gap: reading ≠ writing! 📚 Different skills, same word!"
        }
      ]
    },

    // ===================================================================
    // SET 6: Native Phrases & Slang (10 preguntas)
    // ===================================================================
    {
      id: "m3-u13-set6",
      title: "Native Phrases",
      questions: [
        {
          type: "mc",
          q: "My friend drives like a maniac! He drives very _____.",
          options: ["dangerously", "dangerous", "danger"],
          a: "dangerously",
          tip: "Natives say 'drive like a maniac' for dangerous drivers! Street talk! 🚗💨",
          successtip: "✓ Drives DANGEROUSLY (adverb)! Street slang: 'drives like a maniac' = crazy driver! 🏎️ Dangerous person drives dangerously!"
        },
        {
          type: "mc",
          q: "Take it easy! Don't work so _____!",
          options: ["hard", "hardly", "harder"],
          a: "hard",
          tip: "'Take it easy' = relax, don't stress! Common native phrase! 😌",
          successtip: "✓ Don't work HARD! Native phrase: 'take it easy' = relax! 🛋️ Work-life balance! Chill out!"
        },
        {
          type: "mc",
          q: "She's a straight-A student. She studies very _____.",
          options: ["seriously", "serious", "seriousness"],
          a: "seriously",
          tip: "'Straight-A student' = all A grades! Academic excellence! 📚",
          successtip: "✓ Studies SERIOUSLY (adverb)! School slang: straight-A = perfect grades! 💯 Serious student studies seriously!"
        },
        {
          type: "mc",
          q: "Speak up! I can't hear you _____.",
          options: ["clearly", "clear", "clearer"],
          a: "clearly",
          tip: "'Speak up!' = talk louder! Common command in English! 📢",
          successtip: "✓ Can't hear CLEARLY (adverb)! Native command: 'speak up!' = talk louder! 🔊 Clear sound = hear clearly!"
        },
        {
          type: "mc",
          q: "He's grinding hard! He works _____.",
          options: ["hard", "hardly", "harder"],
          a: "hard",
          tip: "'Grinding' = working hard, hustling! Modern slang! 💪",
          successtip: "✓ Works HARD (adverb)! Street slang: 'grinding' = hustling hard! 💼 Hustle culture is real!"
        },
        {
          type: "mc",
          q: "My boss said 'Good job!' I worked _____.",
          options: ["well", "good", "goodly"],
          a: "well",
          tip: "'Good job!' = praise for work well done! Boss approval! 👍",
          successtip: "✓ Worked WELL (adverb)! Boss praise: 'good job' = you worked well! 💼 Good work = worked well!"
        },
        {
          type: "mc",
          q: "She's a fast learner. She learns _____.",
          options: ["quickly", "quick", "quicker"],
          a: "quickly",
          tip: "'Fast learner' = learns quickly! Common job resume phrase! 🎓",
          successtip: "✓ Learns QUICKLY (adverb)! Job interviews: 'fast learner' = learns quickly! 📖 Quick mind learns quickly!"
        },
        {
          type: "mc",
          q: "Keep it down! You're talking too _____.",
          options: ["loudly", "loud", "louder"],
          a: "loudly",
          tip: "'Keep it down!' = be quiet, lower your voice! Common phrase! 🤫",
          successtip: "✓ Talking LOUDLY (adverb)! House rules: 'keep it down!' = talk quietly! 🔇 Loud talker talks loudly!"
        },
        {
          type: "mc",
          q: "Play it safe! Drive _____.",
          options: ["carefully", "careful", "care"],
          a: "carefully",
          tip: "'Play it safe' = be careful, avoid risks! Safety advice! 🦺",
          successtip: "✓ Drive CAREFULLY (adverb)! Safety phrase: 'play it safe' = be careful! 🚙 Careful driver drives carefully!"
        },
        {
          type: "mc",
          q: "She's killing it at work! She works very _____.",
          options: ["well", "good", "goodly"],
          a: "well",
          tip: "'Killing it' = doing extremely well! Modern success slang! 🔥",
          successtip: "✓ Works WELL (adverb)! Slang: 'killing it' = doing amazing! 🔥 Success = work well!"
        }
      ]
    },

    // ===================================================================
    // SET 7: Workplace & Job Scenarios (10 preguntas)
    // ===================================================================
    {
      id: "m3-u13-set7",
      title: "Workplace English",
      questions: [
        {
          type: "mc",
          q: "I answer the phone _____ at work. Customer service!",
          options: ["politely", "polite", "politeness"],
          a: "politely",
          successtip: "✓ Answer POLITELY (adverb)! Job tip: polite worker answers politely = tips! 📞 Customer service 101!"
        },
        {
          type: "mc",
          q: "My coworker is very _____. She never helps anyone.",
          options: ["lazy", "lazily", "laziness"],
          a: "lazy",
          successtip: "✓ IS lazy (adjective describes person)! Office drama: lazy coworker works lazily! 💼 Adjective for people!"
        },
        {
          type: "mc",
          q: "The manager explained the new rules _____.",
          options: ["clearly", "clear", "clearer"],
          a: "clearly",
          successtip: "✓ Explained CLEARLY (adverb)! Work meetings: clear manager explains clearly! 📋 Clear explanation = no confusion!"
        },
        {
          type: "mc",
          q: "I work the night shift _____ because I have kids.",
          options: ["reluctantly", "reluctant", "reluctance"],
          a: "reluctantly",
          successtip: "✓ Work RELUCTANTLY (adverb) = you don't want to! Parent struggle: night shift = less family time! 😔 Real sacrifice!"
        },
        {
          type: "mc",
          q: "She types _____ and never makes mistakes.",
          options: ["accurately", "accurate", "accuracy"],
          a: "accurately",
          successtip: "✓ Types ACCURATELY (adverb)! Secretary skills: accurate typer types accurately! ⌨️ Accuracy = no errors!"
        },
        {
          type: "mc",
          q: "My boss is _____. He treats everyone _____.",
          options: ["fair / fairly", "fairly / fair", "fair / fair"],
          a: "fair / fairly",
          successtip: "✓ FAIR boss (adjective) treats FAIRLY (adverb)! Good workplace: fair boss = fair treatment! ⚖️ Justice at work!"
        },
        {
          type: "mc",
          q: "I complete my tasks _____ to avoid problems.",
          options: ["efficiently", "efficient", "efficiency"],
          a: "efficiently",
          successtip: "✓ Complete EFFICIENTLY (adverb)! Work smart: efficient worker completes efficiently! 🎯 Time is money!"
        },
        {
          type: "mc",
          q: "The chef cooks _____. The restaurant is always full!",
          options: ["brilliantly", "brilliant", "brilliance"],
          a: "brilliantly",
          successtip: "✓ Cooks BRILLIANTLY (adverb)! Restaurant success: brilliant chef cooks brilliantly! 👨‍🍳 Full house = great food!"
        },
        {
          type: "mc",
          q: "I arrive at work _____ because I take the bus.",
          options: ["early", "earlier", "earliest"],
          a: "early",
          successtip: "✓ Arrive EARLY (adverb, irregular!)! Commute reality: bus schedule = arrive early! 🚌 Better early than late!"
        },
        {
          type: "mc",
          q: "My manager speaks Spanish _____. He helps translate!",
          options: ["fluently", "fluent", "fluency"],
          a: "fluently",
          successtip: "✓ Speaks FLUENTLY (adverb)! Workplace asset: fluent Spanish speaker speaks fluently! 🌎 Bilingual = bonus!"
        }
      ]
    },

    // ===================================================================
    // SET 8: Family & Daily Life (10 preguntas)
    // ===================================================================
    {
      id: "m3-u13-set8",
      title: "Family & Daily Life",
      questions: [
        {
          type: "mc",
          q: "My kids play _____. They break everything!",
          options: ["roughly", "rough", "roughness"],
          a: "roughly",
          successtip: "✓ Play ROUGHLY (adverb)! Parent life: rough kids play roughly! 🏡 Broken toys everywhere!"
        },
        {
          type: "mc",
          q: "My grandmother walks _____. She's 90 years old.",
          options: ["slowly", "slow", "slowness"],
          a: "slowly",
          successtip: "✓ Walks SLOWLY (adverb)! Respect elders: slow grandmother walks slowly! 👵 Patience is love!"
        },
        {
          type: "mc",
          q: "I cook dinner _____ because I'm tired after work.",
          options: ["quickly", "quick", "quicker"],
          a: "quickly",
          successtip: "✓ Cook QUICKLY (adverb)! Working parent: tired = cook quickly! 🍳 Fast food at home!"
        },
        {
          type: "mc",
          q: "My brother is very _____. He eats _____.",
          options: ["hungry / quickly", "hungrily / quick", "hungry / quick"],
          a: "hungry / quickly",
          successtip: "✓ HUNGRY person (adjective) eats QUICKLY (adverb)! Teen boys: always hungry, eat quickly! 🍔 Two different words!"
        },
        {
          type: "mc",
          q: "She sings _____ when she's happy.",
          options: ["loudly", "loud", "louder"],
          a: "loudly",
          successtip: "✓ Sings LOUDLY (adverb)! Happy vibes: loud singing in the shower! 🎤 Happiness = loud songs!"
        },
        {
          type: "mc",
          q: "I clean the house _____ every Saturday.",
          options: ["thoroughly", "thorough", "thoroughness"],
          a: "thoroughly",
          successtip: "✓ Clean THOROUGHLY (adverb)! Weekend chores: thorough cleaning = clean thoroughly! 🧹 Deep clean day!"
        },
        {
          type: "mc",
          q: "My husband snores very _____. I can't sleep!",
          options: ["loudly", "loud", "louder"],
          a: "loudly",
          successtip: "✓ Snores LOUDLY (adverb)! Marriage struggles: loud snorer snores loudly! 😴 Separate rooms needed!"
        },
        {
          type: "mc",
          q: "My daughter studies _____ for her exams.",
          options: ["diligently", "diligent", "diligence"],
          a: "diligently",
          successtip: "✓ Studies DILIGENTLY (adverb)! Proud parent: diligent student studies diligently! 📚 Hard work pays!"
        },
        {
          type: "mc",
          q: "I wake up _____ on weekends. No work!",
          options: ["late", "later", "lately"],
          a: "late",
          successtip: "✓ Wake up LATE (adverb, irregular!)! Weekend life: no alarm = wake late! 😴 Sleep in day!"
        },
        {
          type: "mc",
          q: "My son speaks English _____ than Spanish.",
          options: ["better", "more well", "good"],
          a: "better",
          successtip: "✓ Speaks BETTER (comparative adverb)! Second generation: English better than parents' language! 🌍 Language shift!"
        }
      ]
    },

    // ===================================================================
    // SET 9: Sports & Activities (10 preguntas)
    // ===================================================================
    {
      id: "m3-u13-set9",
      title: "Sports & Activities",
      questions: [
        {
          type: "mc",
          q: "She plays tennis very _____. She wins tournaments!",
          options: ["well", "good", "goodly"],
          a: "well",
          successtip: "✓ Plays WELL (adverb)! Sports success: good player plays well! 🎾 Wins = plays well!"
        },
        {
          type: "mc",
          q: "He runs _____, but his brother runs _____.",
          options: ["fast / faster", "fastly / fastly", "quick / quicker"],
          a: "fast / faster",
          successtip: "✓ Runs FAST (adverb) / FASTER (comparative)! Sibling rivalry: fast runner, faster brother! 🏃 No 'fastly'!"
        },
        {
          type: "mc",
          q: "I swim _____ because I practice every day.",
          options: ["well", "good", "goodly"],
          a: "well",
          successtip: "✓ Swim WELL (adverb)! Pool training: daily practice = swim well! 🏊 Practice makes perfect!"
        },
        {
          type: "mc",
          q: "My team plays _____. We always lose.",
          options: ["badly", "bad", "worse"],
          a: "badly",
          successtip: "✓ Plays BADLY (adverb)! Team struggles: bad team plays badly! ⚽ Lose = play badly!"
        },
        {
          type: "mc",
          q: "She dances very _____. She's a professional!",
          options: ["gracefully", "graceful", "grace"],
          a: "gracefully",
          successtip: "✓ Dances GRACEFULLY (adverb)! Ballet talk: graceful dancer dances gracefully! 💃 Professional skills!"
        },
        {
          type: "mc",
          q: "He throws the ball _____. He's a pitcher!",
          options: ["powerfully", "powerful", "power"],
          a: "powerfully",
          successtip: "✓ Throws POWERFULLY (adverb)! Baseball: powerful arm throws powerfully! ⚾ Strength in action!"
        },
        {
          type: "mc",
          q: "I can't play basketball very _____. I'm too short!",
          options: ["well", "good", "goodly"],
          a: "well",
          successtip: "✓ CAN'T play WELL! Height struggles: short player plays less well! 🏀 Size matters in basketball!"
        },
        {
          type: "mc",
          q: "She skates _____ on the ice. She never falls!",
          options: ["smoothly", "smooth", "smoother"],
          a: "smoothly",
          successtip: "✓ Skates SMOOTHLY (adverb)! Ice skating: smooth skating = skate smoothly! ⛸️ Balance and grace!"
        },
        {
          type: "mc",
          q: "My coach is very _____. He trains us _____.",
          options: ["strict / strictly", "strictly / strict", "strict / strict"],
          a: "strict / strictly",
          successtip: "✓ STRICT coach (adjective) trains STRICTLY (adverb)! Sports discipline: strict training = success! 🏋️ No mercy!"
        },
        {
          type: "mc",
          q: "I stretch _____ before running. No injuries!",
          options: ["carefully", "careful", "care"],
          a: "carefully",
          successtip: "✓ Stretch CAREFULLY (adverb)! Runner wisdom: careful stretching = no injuries! 🤸 Prevention is key!"
        }
      ]
    },

    // ===================================================================
    // SET 10: Mixed Practice - All Types (10 preguntas)
    // ===================================================================
    {
      id: "m3-u13-set10",
      title: "Mixed Practice",
      questions: [
        {
          type: "mc",
          q: "I am very _____. I need to eat _____!",
          options: ["hungry / quickly", "hungrily / quick", "hungry / quick"],
          a: "hungry / quickly",
          successtip: "✓ AM hungry (adjective state) + eat QUICKLY (adverb action)! Hunger = fast eating! 🍔 Different grammar!"
        },
        {
          type: "mc",
          q: "She is a _____ worker. She works very _____.",
          options: ["hard / hard", "hardly / hardly", "hard / hardly"],
          a: "hard / hard",
          successtip: "✓ HARD worker (adjective) works HARD (adverb)! Same word, different use! 💪 Work ethic!"
        },
        {
          type: "mc",
          q: "I can understand English _____, but I speak _____.",
          options: ["well / badly", "good / bad", "well / bad"],
          a: "well / badly",
          successtip: "✓ Understand WELL + speak BADLY (both adverbs)! Learning gap: listening ≠ speaking! 🗣️ Common immigrant struggle!"
        },
        {
          type: "mc",
          q: "My car is very _____. It runs _____.",
          options: ["old / poorly", "old / poor", "older / poorly"],
          a: "old / poorly",
          successtip: "✓ OLD car (adjective) runs POORLY (adverb)! Car troubles: old car = runs poorly! 🚗 Time for repairs!"
        },
        {
          type: "mc",
          q: "She sings _____, but she can't dance _____.",
          options: ["beautifully / well", "beautiful / good", "beautifully / good"],
          a: "beautifully / well",
          successtip: "✓ Sings BEAUTIFULLY + can't dance WELL (both adverbs)! Talents differ: great singer, bad dancer! 🎤💃"
        },
        {
          type: "mc",
          q: "I am a _____ driver, so I drive _____.",
          options: ["safe / safely", "safely / safe", "safe / safe"],
          a: "safe / safely",
          successtip: "✓ SAFE driver (adjective) drives SAFELY (adverb)! Road safety: safe person = safe driving! 🚙 Lives matter!"
        },
        {
          type: "mc",
          q: "He is very _____ because he doesn't eat _____.",
          options: ["thin / well", "thinly / good", "thin / good"],
          a: "thin / well",
          successtip: "✓ IS thin (adjective) BECAUSE doesn't eat WELL (adverb)! Health: bad eating = thin body! 🥗 Eat well!"
        },
        {
          type: "mc",
          q: "I work _____ and sleep _____. It's a good balance!",
          options: ["hard / well", "hardly / good", "hard / good"],
          a: "hard / well",
          successtip: "✓ Work HARD + sleep WELL (both adverbs)! Life balance: hard work needs good sleep! 😴💼 Health is wealth!"
        },
        {
          type: "mc",
          q: "My English is getting _____. I practice _____.",
          options: ["better / daily", "good / day", "better / day"],
          a: "better / daily",
          successtip: "✓ Getting BETTER (comparative) + practice DAILY (adverb)! Progress: daily practice = better English! 📈 Keep going!"
        },
        {
          type: "mc",
          q: "She is a _____ cook, but today the food tastes _____.",
          options: ["good / bad", "well / badly", "good / badly"],
          a: "good / bad",
          successtip: "✓ GOOD cook (adjective) + food tastes BAD (adjective after 'taste')! Off day: good cook, bad meal! 🍳 Happens!"
        }
      ]
    }
    ],

// ===================================================================
// READING SET: Adverbs and Adjectives (Manner and Personality)
// ===================================================================
readingSet: {
  id: "m3-u13-reading",
  title: "Reading: Descriptions and Actions",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Maria's Driving (preguntas 1-2)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Safe Driving**

Maria is a careful driver. She always drives carefully and never has accidents. She thinks that safety is the most important thing when you are behind the wheel.`
    },
    {
      type: "mc",
      q: "How does Maria drive?",
      options: ["Carefully", "Careful", "With care"],
      a: "Carefully",
      successtip: "✓ ADVERB!\nWe use 'carefully' to describe the action of driving. 🚗"
    },
    {
      type: "mc",
      q: "What kind of driver is Maria?",
      options: ["Careful", "Carefully", "Careless"],
      a: "Careful",
      successtip: "✓ ADJECTIVE!\n'Careful' describes Maria (the noun). 👩‍🦰"
    },

    

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: John's Work Ethic (preguntas 3-4)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Hard Work**

John works hard at his job. He arrives early and stays late. His boss is very happy with him and says he works very well. John is a very efficient employee.`
    },
    {
      type: "mc",
      q: "How does John work according to his boss?",
      options: ["Hard and well", "Hardly and good", "Hard and good"],
      a: "Hard and well",
      successtip: "✓ IRREGULARS!\n'Hard' and 'Well' are the correct adverb forms. 'Well' is the adverb of 'good'. 💼"
    },
    {
      type: "mc",
      q: "When does John arrive at work?",
      options: ["Early", "Earlier", "Earliest"],
      a: "Early",
      successtip: "✓ PUNCTUALITY!\n'Early' functions as both an adjective and an adverb. ⏰"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Grandmother's Personality (preguntas 5-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Gentle Grandmother**

My grandmother is very slow. She moves slowly and talks quietly. She is a patient and kind person. Everyone in the family loves her gentle nature.`
    },
    {
      type: "mc",
      q: "How does grandmother talk?",
      options: ["Quietly", "Quiet", "In quiet"],
      a: "Quietly",
      successtip: "✓ MANNER!\nShe talks in a 'quiet' way, so she talks 'quietly'. 👵"
    },
    {
      type: "mc",
      q: "What kind of person is grandmother?",
      options: ["Patient and kind", "Patiently and kindly", "Patience and kindness"],
      a: "Patient and kind",
      successtip: "✓ CHARACTER!\n'Patient' and 'kind' are adjectives that describe her personality. ❤️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Lisa the Singer (preguntas 7-8)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Music and Practice**

Lisa is a good singer. She sings well at parties. Everyone loves her beautiful voice. She practices daily and improves quickly. She wants to be a star one day!`
    },
    {
      type: "mc",
      q: "How does Lisa sing?",
      options: ["Well", "Good", "Goodly"],
      a: "Well",
      successtip: "✓ GRAMMAR CHECK!\nYou 'sing well' (adverb), you are a 'good singer' (adjective). 🎤"
    },
    {
      type: "mc",
      q: "How often does Lisa practice?",
      options: ["Daily", "Day", "Every days"],
      a: "Daily",
      successtip: "✓ FREQUENCY!\n'Daily' means every day. 📆"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 5: The Fast Brother (preguntas 9-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Need for Speed**

My brother drives fast. He's a fast driver, but sometimes he drives dangerously. I tell him to drive more carefully so he stays safe on the road.`
    },
    {
      type: "mc",
      q: "What kind of driver is the brother?",
      options: ["Fast", "Fastly", "Faster"],
      a: "Fast",
      successtip: "✓ IRREGULAR!\n'Fastly' does not exist. 'Fast' is both the adjective and the adverb. 🏎️"
    },
    {
      type: "mc",
      q: "How should the brother drive according to the speaker?",
      options: ["More carefully", "More careful", "Careful"],
      a: "More carefully",
      successtip: "✓ COMPARATIVE ADVERB!\nWe are comparing his current driving with a better way. 🚙"
    }
  ]
},

    readingSet: {
      id: "m3-u13-review",
      title: "Unit 13 Review",
      questions: [
        {
          type: "match",
          q: "Final Review: Match adjectives to adverbs",
          pairs: [
            { left: "slow (adjective)", right: "slowly (adverb)" },
            { left: "good (adjective)", right: "well (adverb)" },
            { left: "fast (both!)", right: "fast (same word!)" }
          ],
          a: "completed",
          successtip: "✓ Adjectives describe PEOPLE/THINGS! Adverbs describe ACTIONS! good→well, slow→slowly, fast→fast! 🎯"
        },
        {
          type: "mc",
          q: "I am very lazy. I walk _____.",
          options: ["slowly", "slow", "slowness"],
          a: "slowly",
          successtip: "✓ Walk SLOWLY (adverb)! Lazy person = slow movement! 🐌 Core lesson mastered!"
        },
        {
          type: "mc",
          q: "She is a _____ singer. She sings _____.",
          options: ["good / well", "well / good", "good / good"],
          a: "good / well",
          successtip: "✓ GOOD singer (adjective) sings WELL (adverb)! Golden rule! 🎤 Never forget!"
        },
        {
          type: "input",
          q: "He runs very _____. (adjective: fast → adverb: ?)",
          a: "fast",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ FAST! Same word for adjective and adverb! NO 'fastly'! 🏃 Irregular adverb!"
        },
        {
          type: "mc",
          q: "My sister has a beautiful turtle. It is very _____.",
          options: ["slow", "slowly", "slowness"],
          a: "slow",
          successtip: "✓ IS slow (adjective)! Turtle = slow! Movement = slowly! 🐢 Context is key!"
        },
        {
          type: "order",
          q: "Order the words: work / I / hard / very",
          words: ["I", "work", "very", "hard."],
          a: "I work very hard.",
          successtip: "✓ I work very HARD! Irregular adverb! Hustle culture! 💪 Grammar + real life!"
        },
        {
          type: "match",
          q: "Match the contexts",
          pairs: [
            { left: "After TO BE", right: "Use adjective (I AM slow)" },
            { left: "After ACTION verb", right: "Use adverb (I WALK slowly)" },
            { left: "With CAN", right: "Use adverb (I CAN speak well)" }
          ],
          a: "completed",
          successtip: "✓ Context determines adjective vs adverb! Master this = master English! 🎓"
        },
        {
          type: "mc",
          q: "I can speak English _____. (good or well?)",
          options: ["well", "good", "goodly"],
          a: "well",
          successtip: "✓ Speak WELL! Job interview answer! 💼 'I speak English well' = hired!"
        },
        {
          type: "mc",
          q: "She is _____, so she speaks _____.",
          options: ["quiet / quietly", "quietly / quiet", "quiet / quiet"],
          a: "quiet / quietly",
          successtip: "✓ QUIET person (adjective) speaks QUIETLY (adverb)! Two forms, one meaning! 🤫"
        },
        {
          type: "mc",
          q: "Complete: I work _____ and sleep _____ = healthy life!",
          options: ["hard / well", "hardly / good", "hard / good"],
          a: "hard / well",
          successtip: "✓ Work HARD + sleep WELL! Life wisdom! 💤💼 You completed Unit 13! Native-level adverbs! 🎉🔥"
        }
      ]
    }
};
